import { PureComponent, createRef } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import autoBind from 'react-autobind';
import isEqual from 'lodash-es/isEqual';
import OrderBy from 'lodash-es/orderBy';
import { isMobile, isIOS } from 'react-device-detect';
import { withRouter } from 'react-router-dom';
import { InView } from 'react-intersection-observer';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faAngleLeft } from '@fortawesome/pro-regular-svg-icons/faAngleLeft';

import { checkAndRoun } from '../../../helpers/topicsHelpers';
import { roundTo } from '../../../helpers/helpers';
import getDomain from '../../../helpers/getDomain';
import TopInvestorChartPopover from '../../UKPrivateCompanyInsights/TopInvestors/TopInvestorChartPopover';
import ConditionalTooltip from '../../UKPrivateCompanyInsights/CommonComponent/ConditionalTooltip';

import Tooltip from '../../Tooltip';
import CFLink from '../CFLink';

import withComponentName from '../../../decorators/withComponentName';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

function Arrow({ direction, ...otherProps }) {
  return (
    <div className={cx('scroll-arrow', direction)} {...otherProps}>
      <FontAwesomeIcon icon={faAngleLeft} />
    </div>
  );
}

const defaultTooltipContentStyles = {
  transform: 'translate(calc(-50% + 9px), calc(-100% - 10px))',
  zIndex: 100,
  minWidth: '80px',
};

export class DataTable extends PureComponent {
  constructor(props) {
    super(props);
    autoBind(this);

    this.state = {
      sortField: null,
      sortOrder: 'asc',
      data: props.data,
      leftArrowVisible: false,
      rightArrowVisible: false,
      total: this.getTotal(props.data),
      isMobile: false,
      isIOS: false,
    };

    this.tableWrapper = createRef();
    this.table = createRef();
  }

  componentDidMount() {
    const { onScroll, onSort } = this.props;

    this.movePage(null, 0);
    window.addEventListener('resize', this.onResize);
    this.setState({ isMobile, isIOS });

    if (onScroll) {
      this.tableWrapper.current.addEventListener('scroll', onScroll, { passive: true });
    }
    if (onSort) {
      const { sortField, sortOrder } = this.props;
      this.setState({
        sortField,
        sortOrder,
      });
    }
  }

  componentDidUpdate({ data: prevData }) {
    const {
      data, onSort, sortField, sortOrder,
    } = this.props;

    if (!isEqual(prevData, data)) {
      this.setState({
        data,
        total: this.getTotal(data),
        sortField: onSort ? sortField : null,
        sortOrder: onSort ? sortOrder : 'asc',
        hoveredElementIndex: -1,
        leftArrowVisible: false,
        rightArrowVisible: false,
      }, this.movePage);
    }
  }

  componentWillUnmount() {
    const { onScroll } = this.props;
    window.removeEventListener('resize', this.onResize);
    if (onScroll) this.tableWrapper.current.removeEventListener('scroll', onScroll);
  }

  onHorizontalScroll() {
    const { scrollLeft, clientWidth, scrollWidth } = this.tableWrapper.current;
    this.setState({
      leftArrowVisible: scrollLeft > 0,
      rightArrowVisible: Math.ceil(scrollLeft + clientWidth) < scrollWidth,
    });
  }

  onResize() {
    setTimeout(this.movePage);
  }

  onMouseEnter({ currentTarget }) {
    const hoveredElementIndex = [...currentTarget.parentNode.children].indexOf(currentTarget);

    this.setState({ hoveredElementIndex });
  }

  onMouseLeave() {
    this.setState({ hoveredElementIndex: -1 });
  }

  getTotal(data) {
    const { fields, withTotal } = this.props;

    if (!withTotal) return [];

    const numericFields = [];
    const result = fields.reduce((data, { field, shouldComputed = false }, index) => {
      if (index !== 0) {
        if (shouldComputed) {
          numericFields.push(field);
          data[field] = 0;
        } else {
          data[field] = '';
        }
      } else {
        data[field] = 'Total';
      }

      return data;
    }, {});

    data.forEach((item) => {
      numericFields.forEach((field) => {
        result[field] += item[field];
      });
    });

    numericFields.forEach((field) => {
      result[field] = result[field].toFixed(2);
    });

    // for computation of custom
    fields.forEach(({ field, totalFormula = null }) => {
      if (typeof totalFormula === 'function') result[field] = totalFormula(data);
    });
    return result;
  }

  getRowData(source, emptyValuesReplacer = '-', hoverEffects = true, isTotal = false) {
    const {
      fields,
      skipRowNumber,
      tableDataClassName,
      location,
      type: tableType,
      homePage,
    } = this.props;
    const { hoveredElementIndex } = this.state;

    const tabHoverEvents = {
      onMouseEnter: this.onMouseEnter,
      onMouseLeave: this.onMouseLeave,
    };

    return fields.map(({
      field,
      type,
      isDate,
      dateFormat,
      skipConverting = false,
      skipColor = false,
      skipSorting,
      className = '',
      isArr,
      allowDynamicRound = false,
    }, index) => {
      const classes = [
        'tab',
        field,
        className,
        tableDataClassName,
        {
          hovered: hoverEffects && hoveredElementIndex === index + (!skipRowNumber ? 1 : 0),
        },
      ];

      switch (type) {
        case 'link':
          return (
            <div
              key={field}
              className={cx(...classes, { 'no-link': !source.url })}
              {...tabHoverEvents}
            >
              {source.url ? (
                <a href={source.url}>
                  {source[field]}
                </a>
              ) : (
                source[field]
              )}
            </div>
          );
        case 'link-advanced': {
          let external;
          try {
            const url = new URL(source.url);
            external = getDomain(url.host) !== getDomain(location.host);
          } catch {
            external = false;
          }

          return (
            <div
              key={field}
              className={cx(...classes, { link: source.url })}
              {...tabHoverEvents}
            >
              {source.url ? (
                <CFLink
                  to={source.url}
                  external={external}
                >
                  {source[field]}
                </CFLink>
              ) : (
                source[field]
              )}
            </div>
          );
        }
        case 'percentage': {
          let value = roundTo(source[field], 2);
          if (value > 100 && isTotal) value = 100;

          return (
            <div
              key={field}
              className={cx(...classes, {
                positive: !skipColor && value > 0,
                negative: !skipColor && value < 0,
              })}
              {...tabHoverEvents}
            >
              {!skipSorting && homePage && (
                <div className={cx('gainersLosers-arrow')}>
                  {(tableType === 'gainers') && (
                    <FontAwesomeIcon icon={faCaretUp} className={cx('asc')} />
                  )}
                  {(tableType === 'losers') && (
                    <FontAwesomeIcon icon={faCaretDown} className={cx('desc')} />
                  )}
                </div>
              )}
              {Number(value) ? `${value}%` : emptyValuesReplacer}
            </div>
          );
        }
        case 'amount':
          return (
            <div
              key={field}
              className={cx(...classes)}
              {...tabHoverEvents}
            >
              {source[field] ? `${!skipConverting
                ? checkAndRoun(source[field], 2, true, true, allowDynamicRound) : roundTo(source[field], 2)}` : emptyValuesReplacer}
            </div>
          );
        case 'currency':
          return (
            <div
              key={field}
              className={cx(...classes)}
              {...tabHoverEvents}
            >
              {source[field] ? `${checkAndRoun(source[field], 2, !skipConverting)}` : emptyValuesReplacer}
            </div>
          );
        case 'topInvestorsChart':
          return (
            <div
              key={field}
              className={cx(...classes)}
              {...tabHoverEvents}
            >
              <>
                {source.name !== 'Total' && (
                  <TopInvestorChartPopover investorData={source} />
                )}
              </>
            </div>
          )
        default: {
          let value = emptyValuesReplacer;
          if (source[field]) {
            if (isDate) {
              value = moment(source[field]).format(dateFormat || 'DD MMM YYYY');
            } else if (isArr) {
              value = source[field].join(', ');
            } else {
              value = source[field];
            }
          }

          return (
            <div
              key={field}
              className={cx(...classes)}
              {...tabHoverEvents}
            >
              {value}
            </div>
          );
        }
      }
    });
  }

  sort(field, extra) {
    const { isDate, type, reverseSort } = extra || {};
    const { sortField, data: oldData } = this.state;
    let { sortOrder } = this.state;
    const { onSort, sortDisable } = this.props;

    if (sortDisable) return;

    let data = oldData;

    if (typeof onSort === 'function') {
      if (reverseSort) {
        sortOrder = sortField === field && sortOrder === 'desc' ? 'asc' : 'desc';
      } else {
        sortOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
      }
      onSort(field, sortOrder);
      this.setState({
        sortOrder,
        sortField: field,
      });
      return;
    }

    if (sortField === field) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      data.reverse();
    } else {
      sortOrder = 'asc';
      if (isDate) {
        data.sort((a, b) => new Date(a[field]).getTime() - new Date(b[field]).getTime());
      } else if (type === 'text') {
        try {
          data = OrderBy(data, [(obj) => obj[field].toLowerCase()]);
        } catch (error) {
          data.sort((a, b) => (a[field] < b[field] ? -1 : Number(a[field] !== b[field])));
        }
      } else {
        data.sort((a, b) => (a[field] < b[field] ? -1 : Number(a[field] !== b[field])));
      }
    }

    this.setState({
      sortOrder,
      data: [...data],
      sortField: field,
    });
  }

  movePage(e, direction = 0) {
    if (e) {
      e.stopPropagation();
      e.nativeEvent.stopPropagation();
    }

    const { scrollLeft, clientWidth } = this.tableWrapper.current;

    switch (direction) {
      case -1: {
        this.tableWrapper.current.scrollTo({
          left: scrollLeft - clientWidth,
          behavior: 'smooth',
        });
        break;
      }
      case 1: {
        this.tableWrapper.current.scrollTo({
          left: scrollLeft + clientWidth,
          behavior: 'smooth',
        });
        break;
      }
      default: {
        this.onHorizontalScroll();
        break;
      }
    }
  }

  render() {
    const {
      sortField,
      sortOrder,
      data,
      total,
      hoveredElementIndex,
      leftArrowVisible,
      rightArrowVisible,
      isMobile,
      isIOS,
    } = this.state;
    const {
      fields,
      withTotal,
      withCryptoTotal,
      type,
      fixedWidthColumns,
      totalTop,
      totalAll,
      showTotalAtTop,
      noMinWidth,
      noMinHeight,
      homePage,
      skipRowNumber,
      tableClass,
      className,
      tableHeaderClassName,
      tableHeaderCellClassName,
      tableBodyClassName,
      tableRowClassName,
      tableDataClassName,
      tableTotalClassName,
      hasBigScreenDesign,
      showPaginationRowNumber,
      currentPage,
      pageSize,
      sortDisable, sortDiableTooltipMessage, sortTooltipProps,
    } = this.props;

    const tabHoverEvents = {
      onMouseEnter: this.onMouseEnter,
      onMouseLeave: this.onMouseLeave,
    };

    const dataTable = cx('data-table', type, className, {
      'fixed-width-columns': fixedWidthColumns,
      'no-min-width': noMinWidth,
      'no-min-height': noMinHeight,
      'home-page': homePage,
      big_screen: hasBigScreenDesign,
    });

    const tableWrapper = cx('table-wrapper', {
      'restyle-scroll': !(isMobile && isIOS),
    });

    const tableTotalClass = cx('tab-row', 'total-panel', tableTotalClassName);

    return (
      <InView
        as="section"
        className={dataTable}
        onChange={this.onHorizontalScroll}
      >
        <div className={tableWrapper} ref={this.tableWrapper} onScroll={this.onHorizontalScroll}>
          <Arrow hidden={!leftArrowVisible} direction="left" onClick={(e) => this.movePage(e, -1)} />
          <div className={cx('tab-table', tableClass)} ref={this.table}>
            <div className={cx('tabs-head')}>
              <div className={cx('tab-row', 'tab-header', tableHeaderClassName)}>
                {!skipRowNumber && (
                  <div className={cx('tab', 'number', tableHeaderCellClassName, { hovered: hoveredElementIndex === 0 })} {...tabHoverEvents}>
                    #
                  </div>
                )}
                {fields.map(({
                  title,
                  tooltip,
                  tooltipContentStyles,
                  field,
                  isDate,
                  type,
                  skipSorting,
                  reverseSort,
                }, index) => (
                  <div
                    key={field}
                    className={(
                      cx(
                        'tab',
                        field,
                        tableHeaderCellClassName,
                        {
                          'skip-sorting': skipSorting,
                          hovered: hoveredElementIndex === index + (!skipRowNumber ? 1 : 0),
                        },
                      )
                    )}
                    onClick={!skipSorting
                      ? () => this.sort(field, { isDate, type, reverseSort })
                      : null}
                    {...tabHoverEvents}
                  >
                    <div className="d-inline-flex align-items-center">
                      <div>
                        {title}
                        {tooltip && (
                          <Tooltip
                            renderOutside
                            hideWithOverflow
                            dynamicPosition
                            contentStyle={tooltipContentStyles ?? defaultTooltipContentStyles}
                            className={Styles.tooltip}
                          >
                            {tooltip}
                          </Tooltip>
                        )}
                      </div>
                      {!skipSorting && !homePage && (
                        <ConditionalTooltip
                          tooltipProps={{
                            renderOutside: true,
                            hideWithOverflow: true,
                            className: cx('sort-arrows-tooltip'),
                            tooltipClassName: cx('sort-arrows-tooltip-content'),
                            ...sortTooltipProps,
                          }}
                          tooltipMessage={sortDiableTooltipMessage}
                          showTooltip={sortDisable}
                        >
                          <div className={cx('sort-arrows')}>
                            {(sortField !== field || sortOrder === 'asc') && (
                              <FontAwesomeIcon icon={faCaretUp} className={cx('asc')} />
                            )}
                            {(sortField !== field || sortOrder === 'desc') && (
                              <FontAwesomeIcon icon={faCaretDown} className={cx('desc')} />
                            )}
                          </div>
                        </ConditionalTooltip>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {Boolean(withTotal && data.length && showTotalAtTop) && (
              <div className={tableTotalClass}>
                <div className={cx('tab', 'number', tableDataClassName)} {...tabHoverEvents}>&nbsp;</div>
                {this.getRowData(total, '', false, true)}
              </div>
            )}
            {Boolean(withCryptoTotal && data.length && totalAll && showTotalAtTop) && (
              <div className={tableTotalClass}>
                <div className={cx('tab', 'number')} {...tabHoverEvents}>&nbsp;</div>
                {this.getRowData(totalAll, '', false, true)}
              </div>
            )}
            <div className={cx('tabs-body', tableBodyClassName)}>
              {data.map((item, index) => (
                <div key={index} className={cx('tab-row', tableRowClassName)}>
                  {!skipRowNumber && (
                    <div className={cx('tab', 'number', { hovered: hoveredElementIndex === 0 }, tableDataClassName)} {...tabHoverEvents}>
                      {showPaginationRowNumber
                        ? ((currentPage - 1) * pageSize) + (index + 1)
                        : (index + 1)}
                    </div>
                  )}
                  {this.getRowData(item)}
                </div>
              ))}
            </div>
            {Boolean(withCryptoTotal && data.length && totalTop) && (
              <div className={tableTotalClass}>
                <div className={cx('tab', 'number')} {...tabHoverEvents}>&nbsp;</div>
                {this.getRowData(totalTop, '', false, true)}
              </div>
            )}
            {Boolean(withCryptoTotal && data.length && totalAll && !showTotalAtTop) && (
              <div className={tableTotalClass}>
                <div className={cx('tab', 'number')} {...tabHoverEvents}>&nbsp;</div>
                {this.getRowData(totalAll, '', false, true)}
              </div>
            )}
            {Boolean(withTotal && data.length && !showTotalAtTop) && (
              <div className={tableTotalClass}>
                <div className={cx('tab', 'number')} {...tabHoverEvents}>&nbsp;</div>
                {this.getRowData(total, '', false, true)}
              </div>
            )}
          </div>
          <Arrow hidden={!rightArrowVisible} direction="right" onClick={(e) => this.movePage(e, 1)} />
        </div>
      </InView>
    );
  }
}

DataTable.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      skipSorting: PropTypes.bool,
      shouldComputed: PropTypes.bool,
      skipConverting: PropTypes.bool,
      totalFormula: PropTypes.func,
    }),
  ).isRequired,
  data: PropTypes.array.isRequired,
  withTotal: PropTypes.bool,
  type: PropTypes.string,
  fixedWidthColumns: PropTypes.bool,
  onScroll: PropTypes.func,
  onSort: PropTypes.func,
  noMinWidth: PropTypes.bool,
  noMinHeight: PropTypes.bool,
  skipRowNumber: PropTypes.bool,
  tableClass: PropTypes.string,
  tableDataClassName: PropTypes.string,
  tableHeaderClassName: PropTypes.string,
  tableHeaderCellClassName: PropTypes.string,
  className: PropTypes.string,
};

DataTable.defaultProps = {
  withTotal: false,
  fixedWidthColumns: false,
  type: '',
  onScroll: null,
  onSort: null,
  noMinWidth: false,
  noMinHeight: false,
  skipRowNumber: false,
  tableClass: '',
  tableDataClassName: '',
  tableHeaderClassName: '',
  tableHeaderCellClassName: '',
  className: '',
};

export default withRouter(withComponentName(DataTable));
